/**
 * Add or edit a receipt voucher (money in) or a payment voucher (money out).
 *
 * A voucher is always from an account to an account (من حساب إلى حساب),
 * both picked the way the chart is laid out - the main account, then the
 * sub-account under it, or every sub-account at once (AccountTreePick):
 *
 *   the box side    a cash box (under 111) or a bank (under 113)
 *   the other side  an expense or income kind (under 4 / 5), or a chart
 *                   account nothing else moves - a car bought (123), an
 *                   advance paid (115), capital put in (31)
 *
 * The other side is required, unless the voucher pays an invoice - the
 * invoice's party is the other side then. The server is told which:
 * category_id or account_id, never both.
 *
 * The box list is fetched fresh every time the form opens rather than taken
 * from the page, so a box added a moment ago - or a page that loaded before
 * the accounting tables existed - never leaves the list empty. Boxes that
 * cannot be used are shown greyed out with the reason, not hidden.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import InputAdornment from "@mui/material/InputAdornment";

import SuiBox from "components/SuiBox";
import DialogShell, { FieldGrid } from "./DialogShell";
import { Field, Pick, Section } from "./Fields";
import PartyPicker from "./PartyPicker";
import AccountTreePick, { boxOptions, kindOptions } from "./AccountTreePick";
import { api, problemOf, today, money, docNo, LOOKUPS } from "./api";

const blank = (type) => ({
  type,
  date: today(),
  source_id: "",
  amount: "",
  // "c:<category id>" or "a:<account id>" - see againstOf
  against: "",
  invoice_id: null,
  party_type: type === "receipt" ? "customer" : "other",
  party_id: null,
  party_name: "",
  reference: "",
  description: "",
});

/** The picker's value for a saved voucher. */
const againstOf = (v) => {
  if (v?.account_id) return `a:${v.account_id}`;
  if (v?.category_id) return `c:${v.category_id}`;
  return "";
};

function VoucherDialog({ open, type, voucher, invoice, lookups, onClose, onSaved }) {
  const [form, setForm] = useState(blank(type));
  const [boxes, setBoxes] = useState(lookups.boxes);
  // the chart accounts a voucher can be against
  const [accounts, setAccounts] = useState([]);
  // the boxes and kinds as chart accounts - where each sits in the tree
  const [links, setLinks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const receipt = type === "receipt";
  const invoiceType = receipt ? "sales" : "purchase";

  useEffect(() => {
    if (!open) return;
    setError("");

    const start = voucher
      ? { ...blank(type), ...voucher, against: againstOf(voucher) }
      : blank(type);

    // opened from an invoice's "record payment": start on that invoice
    if (invoice) {
      Object.assign(start, {
        invoice_id: invoice.id,
        amount: invoice.remaining > 0 ? String(invoice.remaining) : "",
        party_type: invoice.party_type || "other",
        party_id: invoice.party_id,
        party_name: invoice.party_name || "",
      });
    }
    setForm(start);

    const applyBoxes = (list) => {
      setBoxes(list);
      if (invoice && !start.source_id) {
        const box = list.find((b) => b.is_active && b.currency === invoice.currency);
        if (box) setForm((f) => ({ ...f, source_id: f.source_id || box.id }));
      }
    };
    applyBoxes(lookups.boxes);

    api
      .get("/boxes")
      .then((rows) => Array.isArray(rows) && applyBoxes(rows))
      .catch((e) => setError(problemOf(e, "Could not load the boxes")));

    api
      .get("/accounts/postable", { for: "voucher" })
      .then((rows) => setAccounts(Array.isArray(rows) ? rows : []))
      .catch(() => setAccounts([]));

    api
      .get("/accounts/postable", { for: "links" })
      .then((rows) => setLinks(Array.isArray(rows) ? rows : []))
      .catch(() => setLinks([]));

    api
      .get("/invoices", { type: invoiceType })
      .then((rows) => setInvoices(Array.isArray(rows) ? rows : []))
      .catch(() => setInvoices([]));
  }, [open, voucher, invoice]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const pickedInvoice = invoices.find((i) => String(i.id) === String(form.invoice_id)) || null;
  const box = boxes.find((b) => String(b.id) === String(form.source_id));

  /** Why a box cannot be picked right now, or null when it can. */
  const boxBlocked = (b) => {
    if (!b.is_active && String(b.id) !== String(voucher?.source_id)) return "switched off";
    if (pickedInvoice && b.currency !== pickedInvoice.currency)
      return `invoice is ${pickedInvoice.currency}`;
    return null;
  };

  // the kinds and the accounts, in one tree - a switched-off one only when it is this voucher's
  const againstOptions = [
    ...kindOptions(lookups.categories, links, receipt ? "income" : "expense"),
    ...accounts.map((a) => ({ ...a, id: `a:${a.id}` })),
  ].filter((o) => o.is_active || o.id === form.against);
  const boxChoices = boxOptions(boxes, links);

  // invoices still owing, plus the one this voucher already pays
  const openInvoices = invoices.filter(
    (i) => i.remaining > 0.005 || String(i.id) === String(voucher?.invoice_id)
  );

  const pickInvoice = (id) => {
    const inv = invoices.find((i) => String(i.id) === String(id));
    if (!inv) {
      set({ invoice_id: null });
      return;
    }
    const keepBox = box && box.currency === inv.currency;
    const firstBox = boxes.find((b) => b.is_active && b.currency === inv.currency);
    set({
      invoice_id: inv.id,
      amount: String(inv.remaining),
      party_type: inv.party_type || "other",
      party_id: inv.party_id,
      party_name: inv.party_name || "",
      source_id: keepBox ? box.id : firstBox?.id || "",
    });
  };

  const save = async () => {
    if (!form.source_id) return setError("Pick the cash box or bank");
    if (!form.against && !form.invoice_id)
      return setError(
        receipt
          ? "Pick the account it was received for · اختر الحساب المقبوض عنه"
          : "Pick the account it was paid to · اختر الحساب المصروف له"
      );
    if (!(Number(form.amount) > 0)) return setError("Enter the amount");

    setSaving(true);
    setError("");
    const [kind, id] = String(form.against || "").split(":");
    const { against, ...rest } = form;
    const body = {
      ...rest,
      category_id: kind === "c" ? Number(id) : null,
      account_id: kind === "a" ? Number(id) : null,
      amount: Number(form.amount),
    };

    try {
      if (voucher) await api.put(`/vouchers/${voucher.id}`, body);
      else await api.post("/vouchers", body);
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(problemOf(e, "Could not save the voucher"));
    }
    return null;
  };

  const name = receipt ? "Receipt Voucher · سند قبض" : "Payment Voucher · سند صرف";
  const title = voucher ? `${docNo(type, voucher.number)} · ${name}` : `New ${name}`;

  return (
    <DialogShell
      open={open}
      title={title}
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
      maxWidth="md"
    >
      <Section title="From account to account · من حساب إلى حساب">
        <AccountTreePick
          mainLabel={receipt ? "Into · الحساب الرئيسي" : "From · الحساب الرئيسي"}
          label={receipt ? "Into box / bank · إلى" : "From box / bank · من"}
          options={boxChoices}
          value={form.source_id}
          onChange={(id) => set({ source_id: id })}
          getLabel={(b) => [b.code, b.name].filter(Boolean).join(" ")}
          getHint={(b) => boxBlocked(b) || `${money(b.balance)} ${b.currency}`}
          isDisabled={(b) => Boolean(boxBlocked(b))}
          noOptionsText="No boxes yet - add one on the Boxes & Banks tab"
          helperText={box ? `Balance ${money(box.balance)} ${box.currency}` : " "}
        />
        <AccountTreePick
          mainLabel={receipt ? "For · الحساب الرئيسي" : "To · الحساب الرئيسي"}
          label={receipt ? "For account · عن حساب" : "To account · إلى حساب"}
          options={againstOptions}
          value={form.against}
          onChange={(id) => set({ against: id })}
          noOptionsText="No accounts here - add them on the Chart or Categories tab"
          helperText={pickedInvoice && !form.against ? "Optional when paying an invoice" : " "}
        />
      </Section>

      <Section title={receipt ? "The money · المبلغ المقبوض" : "The money · المبلغ المصروف"}>
        <FieldGrid columns={3}>
          <Field
            label="Amount · المبلغ"
            type="number"
            placeholder="0.00"
            value={form.amount}
            onChange={(e) => set({ amount: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {box?.currency || pickedInvoice?.currency || ""}
                </InputAdornment>
              ),
            }}
          />
          <Field
            label="Date · التاريخ"
            type="date"
            value={form.date}
            onChange={(e) => set({ date: e.target.value })}
          />
          <Field
            label="Reference · رقم الشيك / الحوالة"
            placeholder="Optional"
            value={form.reference || ""}
            onChange={(e) => set({ reference: e.target.value })}
          />
        </FieldGrid>
      </Section>

      <Section title={receipt ? "Received from · استلمنا من" : "Paid to · صرفنا إلى"}>
        <SuiBox maxWidth={{ md: "66%" }}>
          <PartyPicker
            value={form}
            onChange={(p) => set(p)}
            parties={lookups.parties}
            label={receipt ? "Received from" : "Paid to"}
          />
        </SuiBox>
      </Section>

      <Section title="Details · التفاصيل">
        <FieldGrid>
          <Pick
            label={`Against ${invoiceType} invoice · عن فاتورة`}
            options={openInvoices}
            value={form.invoice_id ?? ""}
            onChange={pickInvoice}
            getLabel={(i) => `${docNo(i.type, i.number)} · ${i.party_name || "-"}`}
            getHint={(i) => `owes ${money(i.remaining)} ${i.currency}`}
            placeholder="Optional"
            noOptionsText="No open invoices"
          />
        </FieldGrid>
        <Field
          label="Description · البيان"
          placeholder="What it was for"
          value={form.description || ""}
          onChange={(e) => set({ description: e.target.value })}
          multiline
          minRows={2}
        />
      </Section>
    </DialogShell>
  );
}

VoucherDialog.defaultProps = { voucher: null, invoice: null };

VoucherDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(["receipt", "payment"]).isRequired,
  voucher: PropTypes.shape({
    id: PropTypes.number,
    number: PropTypes.number,
    source_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    invoice_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    category_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    account_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  invoice: PropTypes.shape({
    id: PropTypes.number,
    remaining: PropTypes.number,
    currency: PropTypes.string,
    party_type: PropTypes.string,
    party_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    party_name: PropTypes.string,
  }),
  lookups: LOOKUPS.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default VoucherDialog;
